import { useQuery } from "@tanstack/react-query";
import type { FeatureAccessResponse } from "@dealer-center/ddc-pages-configuration-interface";
import {
  CONFIGURATION_QUERY_KEY_BASE,
  configurationRequest
} from "./configurationRequest";

export interface UseGetFeatureAccessOptions {
  userId?: string;
  accountId?: string;
  /** The app's locale, hyphenated (`en-US`). Translated to the wire form here. */
  locale?: string;
}

/**
 * `featureaccess` takes `locale` with an **underscore** (`en_US`); the front-end `Locale` uses a
 * hyphen (`en-US`). Only the separator differs, so a replacement is the whole translation.
 *
 * An unrecognised locale is deliberately **not** an error: Label Services 404s and the BFF still
 * returns `200` with `null` display names. That makes a botched translation silent — the tab renders
 * a page of raw permission codes instead of failing — which is why this is exported and tested
 * directly rather than left inline.
 */
export const toBffLocale = (locale?: string) => locale?.replace("-", "_");

/**
 * Reads the account's entitled feature catalog with the subject's grant state (FR-032), plus the
 * `administrator` flag (FR-034).
 *
 * **This is the only query the Feature Access tab needs.** `administrator` is a sibling of
 * `permissions` on this response — see `FeatureAccessResponse` in the interface lib, the single
 * normative statement of where the field lives. It moved here from the profile response between
 * 2026-08-21 and 2026-08-24, which is what makes the tab independent of `useGetUserInfo`.
 *
 * The rows are the **account catalog**, not the user's grants, so the row count does not shrink for a
 * user who holds few of them. Ungranted rows arrive with `access: null` and `granted: false`.
 */
export const useGetFeatureAccess = ({
  userId,
  accountId,
  locale
}: UseGetFeatureAccessOptions) => {
  const bffLocale = toBffLocale(locale);

  return useQuery({
    // The translated locale is part of the key so switching locale refetches rather than serving
    // the previous language's resolved names from cache.
    queryKey: [
      CONFIGURATION_QUERY_KEY_BASE,
      "featureaccess",
      accountId,
      userId,
      bffLocale
    ],
    queryFn: () => {
      if (!userId || !accountId) {
        throw new Error(
          "useGetFeatureAccess requires both userId and accountId"
        );
      }

      return configurationRequest<FeatureAccessResponse>({
        resource: "featureaccess",
        userId,
        accountId,
        // Omitted entirely when absent, letting the BFF apply its own `en_US` default rather than
        // sending `locale=undefined`. A missing locale must not disable the query.
        params: { locale: bffLocale }
      });
    },
    enabled: Boolean(userId && accountId),
    retry: 1,
    staleTime: 1000 * 60 * 5
  });
};
